import { useCallback, useRef, useState } from "react";
import { campaignApi } from "@/lib/api";
import type { GeocodeResultApi } from "@/lib/api";

interface AddressSearchProps {
  onPick: (result: GeocodeResultApi) => void;
}

const KIND_LABEL: Record<GeocodeResultApi["kind"], string> = {
  LOGRADOURO: "Logradouro",
  LOTE: "Lote cadastral",
  CEP: "CEP",
};

/** Turns the size of what was matched into what it means for the operator. */
function extentHint(result: GeocodeResultApi): string | null {
  // A CEP outside the mapped street layer is placed by the CEP's own coordinate.
  if (result.precision === "CEP") return "posição do CEP — ajuste o marcador até o tronco";
  if (result.extent_m == null) return null;
  if (result.precision === "PARCEL") {
    return `lote de ${Math.round(result.extent_m).toLocaleString("pt-BR")} m²`;
  }
  const km = result.extent_m / 1000;
  const length =
    km >= 1
      ? `${km.toFixed(1).replace(".", ",")} km`
      : `${Math.round(result.extent_m)} m`;
  return `via de ${length} — o ponto cai no meio dela`;
}

/**
 * Address-to-position search (plan §4.3).
 *
 * Deliberately framed as a *proposal*: this base has no house numbers, so the
 * best a street match can do is land somewhere along the street. Saying how
 * long that street is turns a misleading pin into a usable starting point —
 * the operator drags it to the trunk afterwards.
 */
export default function AddressSearch({ onPick }: AddressSearchProps) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<GeocodeResultApi[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const runSearch = useCallback(async () => {
    const q = term.trim();
    if (q.length < 3) {
      setError("Informe ao menos 3 caracteres.");
      return;
    }
    setError(null);
    setSearching(true);
    const current = ++seq.current;
    try {
      const rows = await campaignApi.geocode(q);
      // A slower earlier search must not overwrite a newer result.
      if (current !== seq.current) return;
      setResults(rows);
      setSearched(true);
    } catch {
      if (current !== seq.current) return;
      setResults([]);
      setSearched(true);
      setError("Não foi possível buscar o endereço.");
    } finally {
      if (current === seq.current) setSearching(false);
    }
  }, [term]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-[1fr_auto] gap-2">
        <label className="block">
          <span className="text-xs font-medium text-foreground-700">
            Logradouro, CEP, inscrição cadastral ou número do lote
          </span>
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void runSearch();
              }
            }}
            placeholder="Ex.: Rua S-6, 74823-410, 30404404180000"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm"
          />
        </label>
        <button
          onClick={() => void runSearch()}
          disabled={searching}
          className="self-end px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium cursor-pointer hover:bg-primary-700 disabled:bg-background-300 disabled:cursor-not-allowed"
        >
          {searching ? "Buscando..." : "Buscar"}
        </button>
      </div>

      {error && <p className="text-xs text-danger-600">{error}</p>}

      {searched && !searching && results.length === 0 && !error && (
        <p className="text-xs text-foreground-500">
          Nada encontrado. A busca cobre apenas as camadas já carregadas no
          sistema (logradouros, lotes cadastrais e CEPs) — se o endereço não
          estiver nelas, use o mapa ou as coordenadas.
        </p>
      )}

      {results.length > 0 && (
        <>
          <ul className="rounded-lg border border-background-200 divide-y divide-background-200 max-h-64 overflow-y-auto">
            {results.map((r, i) => (
              <li key={`${r.kind}-${r.label}-${i}`}>
                <button
                  onClick={() => onPick(r)}
                  className="w-full text-left px-3 py-2.5 hover:bg-background-50 cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-background-100 text-foreground-500">
                      {KIND_LABEL[r.kind]}
                    </span>
                    <span className="text-sm text-foreground-900">{r.label}</span>
                  </div>
                  <div className="text-[11px] text-foreground-500 mt-0.5">
                    {[
                      r.kind !== "LOGRADOURO" && r.street ? r.street : null,
                      r.neighborhood,
                      r.cep,
                      extentHint(r),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-start gap-2 rounded-lg bg-warning-50 border border-warning-200 px-3 py-2.5">
            <i className="ri-crosshair-2-line text-warning-600 mt-0.5" />
            <p className="text-xs text-warning-800">
              O endereço define uma <strong>posição proposta</strong>, não a
              posição do tronco: esta base não tem numeração predial. Depois de
              escolher, arraste o marcador até o exemplar.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
